/**
 * FHIR Bundle utilities.
 *
 * ## paginatedSearch — iterate all resources across paginated FHIR search results
 *
 * FHIR search responses come back as Bundles with a limited page size. To walk
 * through all matching resources you normally need a loop that follows `next`
 * links, extracts resources from each page, and stops when there are no more
 * pages. `paginatedSearch` wraps all of that into an async generator so you
 * can use a simple `for await` loop:
 *
 * ```ts
 * import { paginatedSearch } from "@/lib/fhir/bundle";
 *
 * for await (const patient of paginatedSearch<Patient>({
 *   medplum,
 *   resourceType: "Patient",
 *   query: { active: true, _count: 100 },
 * })) {
 *   console.log(patient.id, patient.name);
 * }
 * ```
 *
 * The generator:
 * - Calls `medplum.search()` for the first page, then `medplum.get(nextUrl)` for subsequent pages
 * - Filters entries to only yield resources matching the requested `resourceType`
 * - Stops when a page has no matching resources or no `next` link
 *
 * Combine with `BatchWriter` for paginate-then-write migration patterns:
 *
 * ```ts
 * const batch = new BatchWriter<Observation>(medplum, { dryRun, report, logger });
 *
 * for await (const obs of paginatedSearch<Observation>({
 *   medplum,
 *   resourceType: "Observation",
 *   query: { code: "http://loinc.org|12345", _count: 100 },
 * })) {
 *   const updated = transformObservation(obs);
 *   if (updated) await batch.push(updated);
 * }
 *
 * await batch.flush();
 * ```
 */

import type { MedplumClient, MedplumRequestOptions, QueryTypes } from "@medplum/core";
import type { Bundle, Resource } from "@medplum/fhirtypes";

/**
 * Extracts the next cursor URL from a FHIR Bundle's link array.
 */
export function getNextLink(bundle: Bundle): string | undefined {
  return bundle.link?.find((link) => link.relation === "next")?.url;
}

/**
 * Returns the first resource in a Bundle that matches the requested resource type.
 */
export function getResourceFromBundle<T extends Resource>({
  bundle,
  resourceType,
}: {
  bundle: Bundle;
  resourceType: T["resourceType"];
}): T | undefined {
  const resource = bundle.entry?.find(
    (entry) => entry.resource?.resourceType === resourceType,
  )?.resource;

  return resource as T | undefined;
}

/**
 * Async generator that paginates a FHIR search via Bundle `next` links.
 * Yields individual resources, handling type filtering and empty-page termination.
 *
 * @example
 * ```ts
 * for await (const obs of paginatedSearch<Observation>({ medplum, resourceType: "Observation", query })) {
 *   // process each observation
 * }
 * ```
 */
/**
 * Ensures a search sorts by `_lastUpdated` unless the caller already picked a sort.
 *
 * Medplum only emits stable `_cursor` "next" links when a `_sort` is present; with no sort it
 * paginates by `_offset`, which hard-errors past 10,000 results (`Search offset exceeds maximum`).
 * Defaulting to `_lastUpdated` keeps full-table scans cursor-based and uncapped.
 */
function withLastUpdatedSort(query: QueryTypes): QueryTypes {
  if (!query) {
    return { _sort: "_lastUpdated" };
  }
  if (typeof query === "string") {
    const params = new URLSearchParams(query);
    if (!params.has("_sort")) params.set("_sort", "_lastUpdated");
    return params;
  }
  if (query instanceof URLSearchParams) {
    if (query.has("_sort")) return query;
    const params = new URLSearchParams(query);
    params.set("_sort", "_lastUpdated");
    return params;
  }
  if (Array.isArray(query)) {
    return query.some((pair) => pair?.[0] === "_sort")
      ? query
      : [...query, ["_sort", "_lastUpdated"]];
  }
  return "_sort" in query ? query : { ...query, _sort: "_lastUpdated" };
}

/**
 * Medplum refuses an `_offset` past this many results. A search sorted on anything but
 * `_lastUpdated` pages by offset, so it can't read further.
 */
export const MAX_OFFSET_SCAN_RESULTS = 10_000;

/**
 * Collects search pages until they run out or reach `MAX_OFFSET_SCAN_RESULTS`. It stops before the
 * page request Medplum would refuse, so the caller gets the first 10,000 rows instead of an error.
 * `isAtLimit` says rows may be missing - the caller must log or show it, never drop it silently.
 */
export async function collectPagesUpToScanLimit<T>(
  pages: AsyncIterable<T[]>,
): Promise<{ rows: T[]; isAtLimit: boolean }> {
  const rows: T[] = [];
  for await (const page of pages) {
    rows.push(...page);
    if (rows.length >= MAX_OFFSET_SCAN_RESULTS) {
      return { rows: rows.slice(0, MAX_OFFSET_SCAN_RESULTS), isAtLimit: true };
    }
  }
  return { rows, isAtLimit: false };
}

export async function* paginatedSearch<T extends Resource>({
  medplum,
  resourceType,
  query,
  options,
}: {
  medplum: MedplumClient;
  resourceType: T["resourceType"];
  query: QueryTypes;
  /** Per-request options (headers, abort signal) applied to every page, e.g. `X-Medplum: extended`. */
  options?: MedplumRequestOptions;
}): AsyncGenerator<T> {
  let nextUrl: string | undefined;
  // Force cursor (not offset) pagination so scans don't cap at 10,000 results.
  const initialQuery = withLastUpdatedSort(query);

  while (true) {
    const bundle: Bundle = nextUrl
      ? await medplum.get(nextUrl, options)
      : await medplum.search(
          resourceType as Parameters<MedplumClient["search"]>[0],
          initialQuery,
          options,
        );

    const resources =
      bundle.entry
        ?.map((e) => e.resource)
        .filter((r): r is T => r?.resourceType === resourceType) ?? [];
    if (resources.length === 0) break;

    for (const resource of resources) yield resource;

    nextUrl = getNextLink(bundle);
    if (!nextUrl) break;
  }
}
